import { getRegionalLocaleForCountry, type Locale } from "@/config/locale";

/**
 * Replace the channel segment in a URL path with a new channel slug.
 *
 * URL shape: `/{locale}/{channel}/...` — channel is always the 2nd segment.
 *
 * Why not `pathname.replace(\`/${currentChannel}/\`, \`/${newChannel}/\`)`?
 * That pattern fails on root paths like `/de/eu` (no trailing slash) because
 * the substring `/eu/` is not present. The segment-based approach handles
 * `/de/eu`, `/de/eu/`, `/de/eu/products`, and `/de/eu/products/bpc-157`
 * uniformly.
 */
export function switchChannelPath(pathname: string, newChannel: string): string {
	const segments = pathname.split("/").filter(Boolean);
	if (segments.length >= 2) {
		segments[1] = newChannel;
	} else if (segments.length === 1) {
		// Only a locale in the path (unusual but possible): append the channel.
		segments.push(newChannel);
	} else {
		// Empty path: fall back to /{newChannel}/ — leaves middleware to re-route.
		return `/${newChannel}/`;
	}
	return `/${segments.join("/")}`;
}

/** Replace the locale segment (always the 1st segment) in a URL path. */
function switchLocalePath(pathname: string, newLocale: string): string {
	const segments = pathname.split("/").filter(Boolean);
	if (segments.length === 0) return `/${newLocale}`;
	segments[0] = newLocale;
	return `/${segments.join("/")}`;
}

/**
 * Target of the "go to the Americas shop" suggestion (geo modal and mismatch banner).
 *
 * A visitor whose country has a regional locale for the current base language (MX on `es` or
 * `es-mx`) moves to that regional locale on `/us`: `/es/eu/x` → `/es-mx/us/x`. Every other
 * visitor keeps the current locale and only switches the channel segment.
 */
export function resolveAmericasSwitchTarget(
	pathname: string,
	currentLocale: Locale,
	detectedCountry: string | null,
): { path: string; locale: Locale } {
	const regionalLocale = getRegionalLocaleForCountry(detectedCountry, currentLocale);
	if (!regionalLocale) {
		return { path: switchChannelPath(pathname, "us"), locale: currentLocale };
	}
	return {
		path: switchChannelPath(switchLocalePath(pathname, regionalLocale), "us"),
		locale: regionalLocale,
	};
}
